import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

/** Inactivity timeout in milliseconds (12 minutes) */
const INACTIVITY_TIMEOUT_MS = 12 * 60 * 1000;

/** User events that count as "activity" */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername]               = useState(null);
  const [isChecking, setIsChecking]           = useState(true); // initial session check
  const inactivityTimer                       = useRef(null);

  // ----------------------------------------------------------------
  // Inactivity timer
  // ----------------------------------------------------------------

  const resetTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    inactivityTimer.current = setTimeout(() => {
      doLogout(true); // automatic logout due to inactivity
    }, INACTIVITY_TIMEOUT_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startActivityTracking = useCallback(() => {
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer(); // start timer immediately
  }, [resetTimer]);

  const stopActivityTracking = useCallback(() => {
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
  }, [resetTimer]);

  // ----------------------------------------------------------------
  // Login / Logout
  // ----------------------------------------------------------------

  const doLogin = useCallback(async (user, pass) => {
    const data = await authService.login(user, pass);
    setUsername(data.username);
    setIsAuthenticated(true);
    startActivityTracking();
    return data;
  }, [startActivityTracking]);

  const doLogout = useCallback(async (dueToInactivity = false) => {
    stopActivityTracking();
    await authService.logout();
    setIsAuthenticated(false);
    setUsername(null);
    if (dueToInactivity) {
      // Optional info – AuthContext itself shows no alert;
      // LoginPage can detect the state.
      window.dispatchEvent(new CustomEvent('auth:inactivity-logout'));
    }
  }, [stopActivityTracking]);

  // ----------------------------------------------------------------
  // Global 401 event → logout
  // ----------------------------------------------------------------
  useEffect(() => {
    const handle401 = () => doLogout(false);
    window.addEventListener('auth:unauthorized', handle401);
    return () => window.removeEventListener('auth:unauthorized', handle401);
  }, [doLogout]);

  // ----------------------------------------------------------------
  // Initial session check on app load
  // ----------------------------------------------------------------
  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const result = await authService.checkSession();
        if (result.authenticated) {
          setIsAuthenticated(true);
          setUsername(result.username);
          startActivityTracking();
        } else {
          authService.clearCredentials();
        }
      } catch {
        authService.clearCredentials();
      } finally {
        setIsChecking(false);
      }
    };
    checkInitialSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => stopActivityTracking();
  }, [stopActivityTracking]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isChecking,
        username,
        login:  doLogin,
        logout: doLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/** Hook for the auth context */
export const useAuth = () => useContext(AuthContext);
